import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { post } from '../api';
import { useAuth, useMe } from '../auth';
import { BrandMark } from '../components/Layout';
import { ErrorBox, Modal, useToast } from '../components/ui';

function PasswordForm({ onDone, submitLabel }: { onDone: () => void; submitLabel: string }) {
  const qc = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setError(null);
    if (next.length < 10) return setLocalError('La nueva contraseña debe tener al menos 10 caracteres');
    if (next !== repeat) return setLocalError('Las contraseñas no coinciden');
    setBusy(true);
    try {
      await post('/auth/password', { currentPassword: current, newPassword: next });
      await qc.invalidateQueries({ queryKey: ['me'] });
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} id="password-form">
      {localError && <div className="alert alert-error">{localError}</div>}
      <ErrorBox error={error} />
      <div className="field">
        <label htmlFor="pw-current">Contraseña actual</label>
        <input id="pw-current" className="input" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="pw-new">Nueva contraseña</label>
        <input id="pw-new" className="input" type="password" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} />
        <span className="hint">Mínimo 10 caracteres. Se cerrarán tus otras sesiones.</span>
      </div>
      <div className="field">
        <label htmlFor="pw-repeat">Repite la nueva contraseña</label>
        <input id="pw-repeat" className="input" type="password" autoComplete="new-password" required value={repeat} onChange={(e) => setRepeat(e.target.value)} />
      </div>
      <button className="btn btn-primary" disabled={busy} style={{ width: '100%' }}>
        {busy && <Loader2 size={15} className="spin" />}
        {submitLabel}
      </button>
    </form>
  );
}

export function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  return (
    <Modal title="Cambiar contraseña" onClose={onClose}>
      <div style={{ paddingBottom: 14 }}>
        <PasswordForm
          submitLabel="Guardar contraseña"
          onDone={() => {
            toast('Contraseña actualizada');
            onClose();
          }}
        />
      </div>
    </Modal>
  );
}

/** Pantalla obligatoria cuando el administrador ha marcado que hay que cambiar la contraseña. */
export function ForcedPasswordChange() {
  const { user } = useMe();
  const { logout } = useAuth();
  return (
    <div className="auth-page">
      <div className="card auth-card">
        <div className="brand">
          <BrandMark />
          <div>
            OCR 1SK
            <small>{user.email}</small>
          </div>
        </div>
        <h1>Elige una contraseña nueva</h1>
        <p className="muted" style={{ marginTop: -8 }}>
          Por seguridad, cambia la contraseña temporal antes de continuar.
        </p>
        <PasswordForm submitLabel="Guardar y continuar" onDone={() => undefined} />
        <p style={{ textAlign: 'center', marginTop: 12 }}>
          <button className="btn btn-sm" style={{ width: 'auto' }} onClick={() => void logout()}>
            Salir
          </button>
        </p>
      </div>
    </div>
  );
}
